import { campaignController } from '@/apis/core';
import Modal from '@/components/Modal';
import { BoxV2 as Box, Button, Flex, TextInput } from 'portal-commons';
import React, { useEffect, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import {
  createMaxLengthValidator,
  createRequiredValidator,
  isValidInput,
} from '../../validators';

interface Props {
  open: boolean;
  mno: { id: number; name: string };
  campaignUid: string;
  onClose: () => void;
  onComplete: () => void;
}

interface FormValues {
  explanation: string;
}

const RemoveMnoModal: React.FC<Props> = ({
  open,
  mno,
  campaignUid,
  onClose,
  onComplete,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: {
      explanation: '',
    },
  });
  const disableSubmit = Object.values(errors).some((error) => !!error?.message);
  const {
    field: { value: explanation, onChange: setExplanation },
    fieldState: { error: explanationError },
  } = useController({
    control,
    name: 'explanation',
    defaultValue: '',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter explanation'),
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(2048),
      },
    },
  });

  const onValidHandler = async (data: FormValues) => {
    setIsProcessing(true);
    const response = await campaignController.deleteNetwork(
      campaignUid,
      mno.id,
      data
    );
    if (response) {
      onComplete();
    }
    onClose();
    setIsProcessing(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal open={open} testId="removeMnoModal">
      <Box
        as="h3"
        sx={{
          py: 's',
          fontSize: 'H600',
          fontWeight: 600,
          lineHeight: 'normal',
          color: 't.black80',
          textAlign: 'center',
        }}
      >
        Remove {mno.name}
      </Box>
      <form
        noValidate
        onSubmit={handleSubmit(onValidHandler)}
        style={{ width: '100%' }}
      >
        <Flex sx={{ flexDirection: 'column', gap: 'xs', mt: 8 }}>
          <Box as="p" sx={{ mt: 7, mb: 11 }}>
            Please confirm that you want to remove this MNO from the campaign.
          </Box>
          <TextInput
            label="Explanation (Required)"
            multiline
            minRows={3}
            maxRows={3}
            value={explanation}
            onChange={setExplanation}
            error={!!explanationError?.message}
            note={explanationError?.message}
            style={{ marginBottom: '15px' }}
            maxLength={2048}
          />
        </Flex>
        <Flex sx={{ py: 30, gap: 18, justifyContent: 'center' }}>
          <Button
            variant="outline"
            data-testid="removeMnoCancelButton"
            disabled={isProcessing}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            disabled={isProcessing || disableSubmit}
            data-testid="removeMnoConfirmButton"
            type="submit"
          >
            Confirm
          </Button>
        </Flex>
      </form>
    </Modal>
  );
};

export default RemoveMnoModal;
